import { createFileRoute, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo, useReducer, useState } from 'react';
import { z } from 'zod';
import { HStack, VStack } from '@/components/Layout';
import { AddStepperCompleteDialog } from '@/components/Stepper';
import type { WorkerType } from '@/constants/workerType';
import Processing from '@/features/import-csv/-components/Processing';
import useGetPeopleGroupDetail, { type Membership } from '@/features/people-group/-api';
import { type OrganizationClauseData, OrganizationClauseEdit, RuleCard, RuleClause } from '@/features/rule-and-policy';
import { SETTING_TYPE } from '@/features/worker-type-rule/types';
import { useSelectedWorkerFilter } from '@/hooks/useSelectedWorkerFilter';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { LIST_EMPTY_DATA } from '@/lib/listPage';
import AddStepperNavigation from '../../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../../-components/AddStepperStepTitleCard';
import BasicForm from '../../-components/BasicForm';
import ConfirmPeopleGroup from '../../-components/ConfirmPeopleGroup';
import ReviewTheImpactedRoleDataTableCard from '../../-components/ReviewTheImpactedRoleDataTableCard';
import SetIndividually from '../../-components/SetIndividually';
import SetMembership from '../../-components/SetMembership';
import { PEOPLE_GROUP_CREATE_HASH } from '../../-constants';
import useBasicForm from '../../-hooks/useBasicForm';
import useHash from '../../-hooks/useHash';
import useUpdatePeopleGroup from '../../-mutations/useUpdatePeopleGroup';
import usePreviewPeopleGroup from '../../-queries/usePreviewPeopleGroup';
import individualsConditionReducer from '../../-reducers/individualsConditionReducer';
import type { SelectedMembershipState } from '../../-types';
import useGetPeopleGroups from '../../../../_list/people-groups/-queries/useGetPeopleGroups';
export const Route = createFileRoute('/_staff/settings/permission-and-roles/_form/people-groups/$id/edit/')({
  validateSearch: zodValidator(z.object({
    completeLinkUrl: z.string().optional()
  })),
  component: RouteComponent
});
function RouteComponent() {
  const {
    id: peopleGroupId
  } = useParams({
    from: '/_staff/settings/permission-and-roles/_form/people-groups/$id/edit/'
  });
  const BASE_LINK = `/settings/permission-and-roles/people-groups/${peopleGroupId}/edit`;
  const {
    data
  } = useGetPeopleGroupDetail({
    peopleGroupId
  });
  const {
    hash
  } = useHash();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createPeopleGroup'
  });
  const {
    data: peopleGroupsRes
  } = useGetPeopleGroups({
    search: {
      category: 'ALL',
      pageIndex: 0,
      pageSize: 100
    }
  });
  const {
    basicFormState,
    handleChange,
    isDuplicateName
  } = useBasicForm({
    data,
    existingPeopleGroups: peopleGroupsRes ?? LIST_EMPTY_DATA
  });

  /**
   * NOTE: zustandまたはxstateに移行すべき
   * useState, useReducerを使っていることで、propsのバケツリレーが発生する
   * また、Membership、CustomFilter、IndividualsConditionのstateがそれぞれ独立しているため、
   * ここもまとめるべきかも合わせて検討が必要
   */
  const {
    selectedMemberships,
    changeWorkerFilterSettingType,
    changeWorkerFilterCustomFilter
  } = useSelectedWorkerFilter<Membership, SelectedMembershipState>({
    workerFilters: data.memberships,
    createStateWrapper: (membership: Membership) => ({
      membership
    }),
    extractWorkerFilter: (state: SelectedMembershipState) => state.membership,
    createDefaultWorkerFilter: (workerType: WorkerType) => ({
      id: '',
      workerType,
      settingType: SETTING_TYPE.NO_ONE,
      customFilter: null
    }),
    includeEditingState: false
  });
  const [organizationTreeState, setOrganizationTreeState] = useState<OrganizationClauseData>(() => ({
    nodes: [],
    selectedDepartments: data.organizationUnits.map(unit => ({
      id: unit.id,
      include: unit.include,
      allSubordinates: unit.allSubordinatesIncluded
    })),
    // 適用メンバー種別はセクション全体の値（organizationUnitWorkerTypes）を採用（空配列 = 未選択）
    selectedWorkerTypes: data.organizationUnitWorkerTypes
  }));
  const [individualsConditionState, individualsConditionStateHandler] = useReducer(individualsConditionReducer, {
    includeWorkers: data.individuallyIncluded,
    excludeWorkers: data.individuallyExcluded
  });
  const {
    preview: mutatePreviewPeopleGroup,
    isPending: isPreviewPeopleGroupPending,
    previewPeopleGroupResponse,
    memberList: previewMemberList
  } = usePreviewPeopleGroup();
  const {
    mutate: mutateUpdatePeopleGroup,
    isPending: isUpdatePeopleGroupPending,
    isComplete
  } = useUpdatePeopleGroup({
    peopleGroupId
  });
  const isPending = isPreviewPeopleGroupPending || isUpdatePeopleGroupPending;
  const search = Route.useSearch();
  const completeLinkUrl = useMemo(() => {
    return search.completeLinkUrl || `/settings/permission-and-roles/people-groups/${peopleGroupId}`;
  }, [search.completeLinkUrl, peopleGroupId]);
  return <HStack className="gap-9 p-10 h-[calc(100vh-64px)] w-full">
      <AddStepperNavigation baseLink={BASE_LINK} currentHash={hash} isEdit={true} />

      <VStack className="gap-6 h-full min-w-0 w-full">
        {isPending && <Processing hash={hash} />}

        {!isPending && <AddStepperStepTitleCard hash={hash} />}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.FORM && <BasicForm basicFormState={basicFormState} handleChange={handleChange} isDuplicateName={isDuplicateName} />}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.SET_MEMBERSHIP && <VStack className="overflow-y-auto">
            <RuleCard ruleId={'organization-tree'}>
              <RuleClause clause={{
            id: 'workerType',
            type: 'workerType'
          }} viewContent={<SetMembership selectedMemberships={selectedMemberships} changeWorkerFilterSettingType={changeWorkerFilterSettingType} changeWorkerFilterCustomFilter={changeWorkerFilterCustomFilter} />} />
              <RuleClause clause={{
            id: 'organization',
            type: 'organization'
          }} viewContent={<OrganizationClauseEdit data={organizationTreeState} onChange={setOrganizationTreeState} />} />
              <RuleClause clause={{
            id: 'individually',
            type: 'individual'
          }} viewContent={<SetIndividually state={individualsConditionState} stateHandler={individualsConditionStateHandler} isEditing={true} />} />
            </RuleCard>
          </VStack>}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.CONFIRM_PEOPLE_GROUP && <ConfirmPeopleGroup previewPeopleGroupResponse={previewPeopleGroupResponse} memberList={previewMemberList} />}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.REVIEW_THE_IMPACTED_ROLE && <ReviewTheImpactedRoleDataTableCard roles={data.referencedRoles} isPending={isPending} />}

        {!isPending && <AddStepperPanelProgress baseLink={BASE_LINK} currentHash={hash} basicFormState={basicFormState} selectedMemberships={selectedMemberships} organizationTreeState={organizationTreeState} individualsConditionState={individualsConditionState} mutatePreviewPeopleGroup={mutatePreviewPeopleGroup} mutateCreateOrUpdatePeopleGroup={mutateUpdatePeopleGroup} isEdit={true} isDuplicateName={isDuplicateName} />}

        {isComplete && <AddStepperCompleteDialog completeMessage={t('addStepperCompleteDialog.update.completeMessage')} completeLinkButtonText={t('addStepperCompleteDialog.update.completeLinkButtonText')} completeLinkUrl={completeLinkUrl} />}
      </VStack>
    </HStack>;
}